import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { ReasonType } from "@rooster/types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ICancelDialogProps {
  show: boolean;
  onCancel: (reason?: ReasonType, comment?: string) => void;
  onClose: () => void;
  reasons?: ReasonType[];
  isRequired?: boolean;
  showComment?: boolean;
  loading?: boolean;
}

export const CancelInterviewDialog: React.FC<ICancelDialogProps> = ({
  show,
  onClose,
  onCancel,
  reasons = [],
  isRequired = false,
  showComment = true,
  loading = false
}) => {
  const { t } = useTranslation();
  const [selectedReasonId, setSelectedReasonId] = useState<string>("");
  const [comment, setComment] = useState("");

  const hasReasons = reasons.length > 0;
  const selectedReason = reasons.find((r) => r.id === selectedReasonId);
  const canConfirm = !hasReasons || !isRequired || !!selectedReasonId;

  const handleClose = () => {
    setSelectedReasonId("");
    setComment("");
    onClose();
  };

  const handleCancel = () => {
    onCancel(selectedReason, comment || undefined);
    setSelectedReasonId("");
    setComment("");
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      aria-labelledby="cancel-dialog-title"
      data-testid="cancel-interview-dialog"
      maxWidth={"sm" as Breakpoint}
      fullWidth
    >
      <DialogTitle id="cancel-dialog-title" data-testid="cancel-dialog-title">
        {t("ROOSTER.LINKS.CANCEL_MEETING_ALERT_TITLE", {
          defaultValue: "Cancel this Meeting?"
        })}
      </DialogTitle>
      <DialogContent data-testid="cancel-dialog-content">
        <DialogContentText fontSize={15} sx={{ mb: hasReasons ? 2 : 0 }}>
          {t("ROOSTER.LINKS.CANCEL_MEETING_ALERT_TEXT", {
            defaultValue: "If you cancel this meeting, you cannot undo it."
          })}
        </DialogContentText>

        {hasReasons && !loading && (
          <>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="cancel-reason-label" required={isRequired}>
                {t("ROOSTER.PUBLIC-APP.CANCEL_REASON_LABEL", {
                  defaultValue: "Why are you cancelling?"
                })}
              </InputLabel>
              <Select
                labelId="cancel-reason-label"
                value={selectedReasonId}
                onChange={(e) => setSelectedReasonId(e.target.value as string)}
                label={t("ROOSTER.PUBLIC-APP.CANCEL_REASON_LABEL", {
                  defaultValue: "Why are you cancelling?"
                })}
                inputProps={{ "aria-required": isRequired }}
                data-testid="cancel-reason-select"
              >
                {reasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showComment && (
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                placeholder={t(
                  "ROOSTER.PUBLIC-APP.CANCEL_COMMENT_PLACEHOLDER",
                  {
                    defaultValue:
                      "Anything else you'd like to share? (optional)"
                  }
                )}
                helperText={`${comment.length}/500`}
                sx={{ mt: 2 }}
                data-testid="cancel-comment-field"
              />
            )}

            {!isRequired && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {t("ROOSTER.PUBLIC-APP.REASON_SOFT_PROMPT", {
                  defaultValue:
                    "Your feedback helps us improve the scheduling process."
                })}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions data-testid="cancel-dialog-actions">
        <Button
          size="small"
          onClick={handleClose}
          autoFocus
          data-testid="cancel-dialog-back-button"
        >
          <ArrowBackIcon /> &nbsp;
          {t("ROOSTER.LINKS.CANCEL_INTERVIEW_BUTTON_BACK", {
            defaultValue: "Go back"
          })}
        </Button>
        <Button
          size="small"
          onClick={handleCancel}
          color="error"
          disabled={!canConfirm || loading}
          data-testid="cancel-dialog-confirm-button"
        >
          <CancelIcon /> &nbsp;
          {t("ROOSTER.LINKS.CANCEL_MEETING_BUTTON_CANCEL", {
            defaultValue: "Cancel Meeting"
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
